import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ComposeDialog } from "@/components/ComposeDialog";
import {
  Search, Loader2, RefreshCw, Trash2, FileEdit, Mails, Plug, Mail,
  PenSquare, MailCheck, CheckSquare, Filter, ReplyAll, Info, Sparkles,
} from "lucide-react";
import { FOLDERS, PRIORITIES, PAGE, threadKey } from "./inbox/inboxUtils";
import { ProgressRow } from "./inbox/ProgressRow";
import { EmailList } from "./inbox/EmailList";
import { EmailDetail } from "./inbox/EmailDetail";
import { ThreadDialog } from "./inbox/ThreadDialog";
import { ReplyDialog } from "./inbox/ReplyDialog";

export default function InboxPage() {
  const { catLabel, catStyle, categories } = useCategories();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [trialLimited, setTrialLimited] = useState(false);
  const [activeAccount, setActiveAccount] = useState("all");
  const [usedCatKeys, setUsedCatKeys] = useState([]);
  const scopedCategories = useMemo(() => (
    activeAccount === "all"
      ? categories
      : categories.filter((c) => c.source !== "ai" || c.account_id === activeAccount)
  ), [categories, activeAccount]);
  // Unione: categorie AI/scope + tutte quelle realmente presenti sulle email (incluse legacy),
  // così ogni chip visibile in lista è anche filtrabile dal dropdown.
  const filterCatKeys = useMemo(() => {
    const seenKeys = new Set();
    const seenLabels = new Set();
    const out = [];
    const add = (k) => {
      if (!k || k === "altro" || seenKeys.has(k)) return;
      const label = (catLabel(k) || k).trim().toLowerCase();
      if (seenLabels.has(label)) return; // evita duplicati con lo stesso nome
      seenKeys.add(k); seenLabels.add(label); out.push(k);
    };
    for (const c of scopedCategories) add(c.key);
    for (const k of usedCatKeys) add(k);
    // Massimo 8 categorie nella tendina, "Altro" inclusa
    const capped = out.slice(0, 7);
    capped.push("altro");
    return capped;
  }, [scopedCategories, usedCatKeys, catLabel]);
  const [activeFolder, setActiveFolder] = useState("inbox");
  const [filter, setFilter] = useState("tutti");
  const [readFilter, setReadFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [q, setQ] = useState("");
  const [highlightTerm, setHighlightTerm] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showHtml, setShowHtml] = useState(true);
  const [htmlHeight, setHtmlHeight] = useState(180);
  const [replyAll, setReplyAll] = useState(false);
  const [jobs, setJobs] = useState({ sync: { state: "idle" }, analyze: { state: "idle" }, pending: 0 });
  const [checkingNew, setCheckingNew] = useState(false);
  const [leavingIds, setLeavingIds] = useState([]);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);
  const [rewritingReply, setRewritingReply] = useState(null);
  const [appliedDraftTone, setAppliedDraftTone] = useState(null);
  const [appliedReplyTone, setAppliedReplyTone] = useState(null);
  const [smart] = useState(true);
  const [aiSearch, setAiSearch] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [checked, setChecked] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeInit, setComposeInit] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadTitle, setThreadTitle] = useState("");
  const loadedRef = useRef(PAGE);
  const listScrollRef = useRef(null);
  const autoAnalyzeRef = useRef(false);
  const autoCheckRef = useRef(false);
  const qRef = useRef("");
  const activeFolderRef = useRef(activeFolder);
  const activeAccountRef = useRef(activeAccount);
  const itemsCacheRef = useRef({});
  const lastPaintedKeyRef = useRef("");
  const isInbox = activeFolder === "inbox";

  const buildParams = useCallback(() => {
    // I filtri categoria/lette/priorità NON vengono più inviati al server: applichiamo il filtro
    // lato client sulle email già caricate della cartella, così il cambio filtro è ISTANTANEO
    // (nessuna chiamata di rete). Il server carica per cartella/account e, se serve, altre pagine.
    const params = { folder: activeFolder, account_id: activeAccount };
    if (qRef.current) params.q = qRef.current;
    if (qRef.current && aiSearch) params.ai = "1";
    else if (qRef.current && smart) params.smart = "1";
    return params;
  }, [activeFolder, activeAccount, smart, aiSearch]);

  // Filtro istantaneo lato client (categoria, lette/non lette, priorità) sulle email caricate.
  const filteredItems = useMemo(() => {
    // I filtri (categoria/lette/priorità) hanno i controlli SOLO in Posta in arrivo:
    // non devono applicarsi a Inviata/Bozze/Cestino, altrimenti (es. filtro priorità attivo)
    // nasconderebbero tutte le email di quelle cartelle che non hanno quella proprietà.
    if (!isInbox || (filter === "tutti" && readFilter === "all" && priorityFilter === "all")) return items;
    return items.filter((it) => {
      if (filter !== "tutti" && (it.category || "da_analizzare") !== filter) return false;
      if (readFilter === "unread" && it.is_read) return false;
      if (readFilter === "read" && !it.is_read) return false;
      if (priorityFilter !== "all" && (it.priority || "") !== priorityFilter) return false;
      return true;
    });
  }, [items, filter, readFilter, priorityFilter, isInbox]);

  const threadCounts = useMemo(() => {
    const m = {};
    for (const it of items) { const k = threadKey(it.subject); if (k) m[k] = (m[k] || 0) + 1; }
    return m;
  }, [items]);

  const load = useCallback(async () => {
    const params = buildParams();
    const key = `${params.folder}:${params.account_id}:${params.q || ""}:${params.smart || ""}:${params.ai || ""}`;
    // Paint ISTANTANEO dalla cache (stale-while-revalidate) al cambio cartella/casella,
    // poi rivalida in background: il cambio di casella/cartella risulta immediato.
    const cached = itemsCacheRef.current[key];
    const isSwitch = lastPaintedKeyRef.current !== key;
    if (isSwitch) {
      // Cambio cartella/casella: paint ISTANTANEO dalla cache (stale-while-revalidate) o svuota.
      if (cached) {
        setItems(cached.items);
        loadedRef.current = cached.items.length;
        setHasMore(cached.hasMore);
        if (typeof cached.total === "number") setTotal(cached.total);
      } else {
        setItems([]);
      }
      lastPaintedKeyRef.current = key;
    }
    const limit = Math.max(PAGE, loadedRef.current);
    const [r, c] = await Promise.all([
      api.get("/communications", { params: { ...params, skip: 0, limit } }),
      api.get("/communications/count", { params }).catch(() => ({ data: {} })),
    ]);
    const data = r.data || [];
    const hasMoreNow = data.length >= limit;
    const totalNow = typeof c.data.total === "number" ? c.data.total : cached?.total;
    itemsCacheRef.current[key] = { items: data, hasMore: hasMoreNow, total: totalNow };
    // Ignora il risultato se nel frattempo l'utente ha cambiato cartella/casella (evita race).
    if (activeFolderRef.current !== params.folder || activeAccountRef.current !== params.account_id) return;
    setItems(data);
    setHighlightTerm(qRef.current && smart ? qRef.current : "");
    loadedRef.current = data.length;
    setHasMore(hasMoreNow);
    if (typeof totalNow === "number") setTotal(totalNow);
  }, [buildParams, smart]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = { ...buildParams(), limit: PAGE };
      // Paginazione: la ricerca AI ordina per pertinenza (offset), le altre usano il cursore `before`.
      if (aiSearch && qRef.current) {
        params.skip = items.length;
      } else {
        const last = items[items.length - 1];
        if (last?.created_at) params.before = last.created_at;
        else params.skip = loadedRef.current;
      }
      const r = await api.get("/communications", { params });
      const data = r.data || [];
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = prev.concat(data.filter((x) => !seen.has(x.id)));
        loadedRef.current = merged.length;
        return merged;
      });
      setHasMore(data.length >= PAGE);
    } catch (e) { console.debug("loadMore failed", e); }
    finally { setLoadingMore(false); }
  }, [loadingMore, hasMore, buildParams, smart, aiSearch, items]);

  const onListScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 320) loadMore();
  }, [loadMore]);

  const loadAccounts = useCallback(() => api.get("/accounts/summary").then((r) => setAccounts(r.data.accounts || [])).catch((e) => console.debug("acc summary", e)), []);

  const openComposeNew = () => {
    // La firma è gestita dinamicamente nell'editor in base alla casella scelta ("Da").
    setComposeInit(activeAccount ? { from_account_id: activeAccount } : null);
    setComposeOpen(true);
  };

  const editDraft = async (item) => {
    const folderAtCall = activeFolder;
    try {
      const r = await api.get(`/communications/${item.id}`);
      // Se nel frattempo l'utente ha cambiato cartella (es. è passato in Posta in arrivo),
      // NON aprire più l'editor della bozza (evita che si apra una bozza sopra l'inbox).
      if (activeFolderRef.current !== folderAtCall || activeFolderRef.current !== "drafts") return;
      const d = r.data;
      setComposeInit({
        draft_id: d.id, to: d.recipient || "", cc: d.cc || "", bcc: d.bcc || "",
        subject: (d.subject === "(nessun oggetto)" ? "" : d.subject) || "", body_html: d.body_html || (d.body ? `<div>${d.body}</div>` : ""),
        attachments: (d.attachment_ids || []).map((id, i) => ({ id, filename: (d.attachments || [])[i] || "allegato" })),
        from_account_id: d.account_id,
      });
      setComposeOpen(true);
    } catch { toast.error("Impossibile aprire la bozza"); }
  };

  const openThread = useCallback(async (item) => {
    const key = item.thread_key;
    if (!key) return;
    setThreadTitle(item.subject || "Conversazione");
    setThreadOpen(true);
    setThreadLoading(true);
    setThreadMsgs([]);
    try {
      const r = await api.get("/communications/thread", { params: { key, account_id: activeAccount } });
      setThreadMsgs(r.data.messages || []);
    } catch (e) { console.debug("thread load failed", e); }
    finally { setThreadLoading(false); }
  }, [activeAccount]);

  const select = useCallback(async (item) => {
    if (item.folder === "drafts") { editDraft(item); return; }
    setSelected(item);
    setAppliedDraftTone(null);
    setDraft(item.draft_reply || "");
    setShowHtml(true);
    setReplyAll(false);
    setDetailLoading(item.folder === "inbox" || !item.folder);
    try {
      const r = await api.get(`/communications/${item.id}`);
      setSelected({ ...r.data, attachment_match: item.attachment_match || null });
      setDraft(r.data.draft_reply || "");
    } finally {
      setDetailLoading(false);
      load();
      loadAccounts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, loadAccounts]);

  useEffect(() => { setSelected(null); setSelectMode(false); setChecked([]); }, [activeFolder, activeAccount]);
  // Reset dei filtri (categoria/lette/priorità) quando si cambia cartella o casella,
  // così un filtro impostato in una casella non resta attivo passando altrove.
  useEffect(() => { setFilter("tutti"); setReadFilter("all"); setPriorityFilter("all"); }, [activeFolder, activeAccount]);

  useEffect(() => { if (!compose) setAppliedReplyTone(null); }, [compose]);

  // Deep-link: apri direttamente l'email indicata da ?mail=<id> (es. dal toast email)
  const mailLinkedRef = useRef(false);
  useEffect(() => {
    const mid = searchParams.get("mail");
    if (!mid || mailLinkedRef.current) return;
    mailLinkedRef.current = true;
    (async () => {
      try {
        const r = await api.get(`/communications/${mid}`);
        select(r.data);
      } catch { toast.error("Email non trovata"); }
      finally {
        searchParams.delete("mail");
        setSearchParams(searchParams, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, select]);
  useEffect(() => { activeFolderRef.current = activeFolder; }, [activeFolder]);
  useEffect(() => { activeAccountRef.current = activeAccount; }, [activeAccount]);
  // Reset del contesto server solo al cambio cartella/account/ricerca (non al cambio filtro client).
  useEffect(() => { loadedRef.current = PAGE; setHasMore(false); if (listScrollRef.current) listScrollRef.current.scrollTop = 0; }, [activeFolder, activeAccount, smart, aiSearch]);
  // Al cambio filtro client basta riportare la lista in cima: nessuna chiamata al server.
  useEffect(() => { if (listScrollRef.current) listScrollRef.current.scrollTop = 0; }, [filter, readFilter, priorityFilter]);
  // Se un filtro riduce troppo i risultati visibili, carica altre pagine della cartella finché
  // non ci sono abbastanza corrispondenze (mantiene la completezza senza rallentare il click).
  useEffect(() => {
    if (hasMore && !loadingMore && items.length > 0 && filteredItems.length < PAGE) loadMore();
  }, [filteredItems.length, hasMore, loadingMore, items.length, loadMore]);
  // All'apertura di Posta inviata/Bozze/Cestino: sincronizza ON-DEMAND quella cartella dal server
  // (non dipende dal loop di background) e ricarica la lista quando arrivano email nuove.
  useEffect(() => {
    if (!["sent", "drafts", "trash"].includes(activeFolder)) return;
    let cancelled = false;
    api.post("/imap/sync-folder", { folder: activeFolder })
      .then((r) => { if (!cancelled && r.data && r.data.imported) load(); })
      .catch(() => { /* noop */ });
    return () => { cancelled = true; };
  }, [activeFolder, activeAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  useEffect(() => {
    api.get("/communications/used-categories", { params: { account_id: activeAccount } })
      .then((r) => setUsedCatKeys(r.data.keys || []))
      .catch((e) => console.debug("used-categories failed", e));
  }, [activeAccount]);

  useEffect(() => {
    // Auto-selezione della prima email SOLO in Posta in arrivo e SOLO se l'elemento appartiene davvero
    // all'inbox (evita di selezionare una bozza rimasta in lista durante il cambio cartella → apriva
    // per errore l'editor "Modifica bozza" sopra l'inbox).
    if (window.matchMedia("(min-width: 768px)").matches && items.length && !selected
        && activeFolder === "inbox" && (items[0].folder === "inbox" || !items[0].folder)) {
      select(items[0]);
    }
  }, [items, selected, select, activeFolder]);

  const approve = async (content) => {
    setBusy(true);
    try {
      await api.put(`/communications/${selected.id}/draft`, { draft_reply: content?.text ?? draft, draft_html: content?.html ?? null });
      const r = await api.post(`/communications/${selected.id}/send`, { reply_all: replyAll, attachment_ids: content?.attachment_ids ?? [] });
      const ccInfo = r.data.sent_cc?.length ? ` (+${r.data.sent_cc.length} in copia)` : "";
      toast.success(`Risposta inviata a ${r.data.sent_to}${ccInfo}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio fallito. Verifica la configurazione SMTP.");
    } finally { setBusy(false); }
  };

  const saveDraft = async (content) => {
    const r = await api.put(`/communications/${selected.id}/draft`, { draft_reply: content?.text ?? draft, draft_html: content?.html ?? null });
    toast.success(r.data.corrected ? "Modifica salvata — l'AI apprenderà dalla correzione" : "Bozza salvata");
    await refresh();
  };

  const createTicket = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/communications/${selected.id}/ticket`);
      toast.success(`Ticket ${r.data.ref} creato`);
      await refresh();
    } finally { setBusy(false); }
  };

  const openPratica = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/pratiche/from-communication/${selected.id}`);
      toast.success(selected.pratica_id ? `Apro la pratica ${r.data.ref}` : `Pratica ${r.data.ref} aperta`);
      navigate(`/pratiche?id=${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore apertura pratica"); }
    finally { setBusy(false); }
  };

  const changePriority = async (p) => {
    try {
      await api.put(`/communications/${selected.id}/priority`, { priority: p });
      setSelected((s) => ({ ...s, priority: p }));
      toast.success(`Priorità aggiornata: ${PRIORITIES.find((x) => x.key === p)?.label}`);
      load();
    } catch { toast.error("Errore nell'aggiornamento della priorità"); }
  };

  const toggleRead = async () => {
    if (!selected) return;
    const newRead = !selected.is_read;
    try {
      await api.put(`/communications/${selected.id}/read`, { read: newRead });
      setSelected((s) => ({ ...s, is_read: newRead }));
      toast.success(newRead ? "Segnata come letta" : "Segnata come da leggere");
      load(); loadAccounts();
    } catch { toast.error("Errore nell'aggiornamento dello stato"); }
  };

  const toggleReadItem = useCallback(async (it) => {
    const newRead = !it.is_read;
    try {
      await api.put(`/communications/${it.id}/read`, { read: newRead });
      setSelected((s) => (s?.id === it.id ? { ...s, is_read: newRead } : s));
      toast.success(newRead ? "Segnata come letta" : "Segnata come da leggere");
      load(); loadAccounts();
    } catch { toast.error("Errore nell'aggiornamento dello stato"); }
  }, [load, loadAccounts]);

  const deleteItem = useCallback(async (it) => {
    setLeavingIds((s) => [...s, it.id]);
    await new Promise((res) => setTimeout(res, 280));
    try {
      const r = await api.delete(`/communications/${it.id}`);
      toast.success(r.data.permanent ? "Email eliminata definitivamente" : "Email spostata nel Cestino");
      setSelected((cur) => (cur?.id === it.id ? null : cur));
      await load();
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setLeavingIds((s) => s.filter((x) => x !== it.id));
    }
  }, [load, loadAccounts]);

  const deleteMail = async () => {
    if (!selected) return;
    setBusy(true);
    try { await deleteItem(selected); } finally { setBusy(false); }
  };

  const restoreItem = useCallback(async (it) => {
    try {
      await api.post(`/communications/${it.id}/restore`);
      toast.success("Email ripristinata nella Posta in arrivo");
      setSelected((cur) => (cur?.id === it.id ? null : cur));
      await load();
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il ripristino");
    }
  }, [load, loadAccounts]);

  const restoreMail = async () => {
    if (!selected) return;
    setBusy(true);
    try { await restoreItem(selected); } finally { setBusy(false); }
  };

  const openCompose = useCallback(async (it, mode) => {
    try {
      const { data: d } = await api.get(`/communications/${it.id}`);
      const other = isInbox ? (d.sender || "") : (d.recipient || d.sender || "");
      const when = (d.email_date || d.created_at || "").replace("T", " ").slice(0, 16);
      const original = d.body_html || (d.body ? `<div>${d.body.replace(/\n/g, "<br>")}</div>` : "");
      const atts = (d.attachment_ids || []).map((id, i) => ({ id, filename: (d.attachments || [])[i] || "allegato" }));
      // La firma è gestita dinamicamente nell'editor (ComposeDialog) in base alla casella scelta.
      if (mode === "forward") {
        const subj = (d.subject || "").toLowerCase().startsWith("fwd:") ? d.subject : `Fwd: ${d.subject || ""}`;
        const fwd = `<div><br></div><div style="color:#57606a;font-size:12px">---------- Messaggio inoltrato ----------<br>Da: ${d.sender_name || d.sender || ""} &lt;${d.sender || ""}&gt;<br>Data: ${when}<br>Oggetto: ${d.subject || ""}</div><br>${original}`;
        setComposeInit({ to: "", cc: "", subject: subj, body_html: fwd, attachments: atts, from_account_id: d.account_id });
      } else {
        const subj = (d.subject || "").toLowerCase().startsWith("re:") ? d.subject : `Re: ${d.subject || ""}`;
        let cc = "";
        let bcc = "";
        if (mode === "replyAll") {
          const otherAddr = (other || "").toLowerCase();
          const extras = [d.recipient, d.cc].filter(Boolean).join(",").split(",").map((s) => s.trim()).filter(Boolean);
          cc = [...new Set(extras.filter((a) => a.toLowerCase() !== otherAddr))].join(", ");
          // I destinatari originali in CCN tornano in CCN (nascosti tra loro), es. comunicazioni ai condòmini.
          const bccList = (d.bcc || "").split(",").map((s) => s.trim()).filter(Boolean);
          bcc = [...new Set(bccList.filter((a) => a.toLowerCase() !== otherAddr))].join(", ");
        }
        const draftPart = d.draft_reply ? `<div>${d.draft_reply.replace(/\n/g, "<br>")}</div>` : "<div><br></div>";
        const quoted = `${draftPart}<div><br></div><blockquote style="margin:0 0 0 6px;padding-left:12px;border-left:3px solid #d0d7de;color:#57606a"><div style="font-size:12px;margin-bottom:6px">Il ${when}, ${d.sender_name || d.sender || ""} &lt;${d.sender || ""}&gt; ha scritto:</div>${original}</blockquote>`;
        setComposeInit({ to: other, cc, bcc, subject: subj, body_html: quoted, in_reply_to: d.id, from_account_id: d.account_id });
      }
      setComposeOpen(true);
    } catch { toast.error("Errore nel caricamento dell'email"); }
  }, [isInbox]);

  const onReply = useCallback((it) => openCompose(it, "reply"), [openCompose]);
  const onReplyAll = useCallback((it) => openCompose(it, "replyAll"), [openCompose]);
  const onForward = useCallback((it) => openCompose(it, "forward"), [openCompose]);

  const rewriteReplyTone = async (tone) => {
    const text = (compose?.body || "").trim();
    if (!text) { toast.error("Scrivi prima un testo da riscrivere"); return; }
    setRewritingReply(tone);
    try {
      const r = await api.post("/compose/rewrite", { text, tone });
      setCompose((c) => ({ ...c, body: r.data.text || c.body }));
      setAppliedReplyTone(tone);
      toast.success(`Testo riscritto in tono ${tone}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Riscrittura fallita"); }
    finally { setRewritingReply(null); }
  };

  const rewriteDraftTone = async (tone, text) => {
    text = (text || "").trim();
    if (!text) { toast.error("Scrivi prima un testo da riscrivere"); return null; }
    setRewritingReply(tone);
    try {
      const r = await api.post("/compose/rewrite", { text, tone });
      setAppliedDraftTone(tone);
      toast.success(`Testo riscritto in tono ${tone}`);
      return r.data.text || text;
    } catch (e) { toast.error(e.response?.data?.detail || "Riscrittura fallita"); return null; }
    finally { setRewritingReply(null); }
  };

  const sendCompose = async () => {
    setSending(true);
    try {
      const r = await api.post(`/communications/${compose.commId}/compose-send`, {
        to: compose.to, cc: compose.cc || "", subject: compose.subject, body: compose.body, mode: compose.mode,
      });
      toast.success(`${compose.mode === "forward" ? "Inoltrata" : "Risposta inviata"} a ${r.data.sent_to}`);
      setCompose(null);
      load(); loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio fallito. Verifica la configurazione SMTP.");
    } finally { setSending(false); }
  };

  const refresh = async () => {
    const r = await api.get(`/communications/${selected.id}`);
    setSelected(r.data);
    load();
    loadAccounts();
  };

  const runSearch = (e) => { e.preventDefault(); qRef.current = q; loadedRef.current = PAGE; setShowSuggest(false); load(); };

  const applySuggestion = (s) => { setQ(s); qRef.current = s; loadedRef.current = PAGE; setShowSuggest(false); load(); };

  useEffect(() => {
    if (q.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/communications/suggest", { params: { q, account_id: activeAccount } });
        setSuggestions(r.data.suggestions || []);
      } catch { setSuggestions([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, activeAccount]);

  const toggleChecked = useCallback((id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id])), []);
  const allChecked = items.length > 0 && checked.length === items.length;
  const toggleSelectAll = () => setChecked(allChecked ? [] : items.map((it) => it.id));
  const exitSelectMode = () => { setSelectMode(false); setChecked([]); };

  const bulkDelete = async () => {
    if (!checked.length) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/communications/bulk-delete", { ids: checked });
      toast.success(`${r.data.deleted} email eliminate`);
      if (checked.includes(selected?.id)) setSelected(null);
      exitSelectMode();
      await load(); loadAccounts();
    } catch { toast.error("Errore durante l'eliminazione multipla"); }
    finally { setBulkBusy(false); }
  };

  const bulkMarkRead = async (read) => {
    if (!checked.length) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/communications/bulk-read", { ids: checked, read });
      toast.success(`${r.data.updated} email segnate come ${read ? "lette" : "da leggere"}`);
      exitSelectMode();
      await load(); loadAccounts();
    } catch { toast.error("Errore nell'aggiornamento multiplo"); }
    finally { setBulkBusy(false); }
  };

  const bulkReplyAll = async () => {
    if (!checked.length) return;
    setBulkBusy(true);
    try {
      const addrs = [];
      const seen = new Set();
      let fromAccount;
      for (const id of checked) {
        try {
          const { data: d } = await api.get(`/communications/${id}`);
          if (!fromAccount && d.account_id) fromAccount = d.account_id;
          const parts = [d.sender, d.recipient, d.cc].filter(Boolean).join(",")
            .split(",").map((s) => s.trim()).filter(Boolean);
          for (const a of parts) {
            const k = a.toLowerCase();
            if (!seen.has(k)) { seen.add(k); addrs.push(a); }
          }
        } catch { /* salta email non caricabile */ }
      }
      if (!addrs.length) { toast.error("Nessun destinatario trovato nelle email selezionate."); return; }
      const to = addrs[0];
      const cc = addrs.slice(1).join(", ");
      setComposeInit({ to, cc, subject: "", body_html: "<div><br></div>", from_account_id: fromAccount || (activeAccount !== "all" ? activeAccount : undefined) });
      setComposeOpen(true);
      exitSelectMode();
    } catch { toast.error("Errore nella preparazione della risposta."); }
    finally { setBulkBusy(false); }
  };

  const composeSent = () => { load(); loadAccounts(); };

  useEffect(() => {
    let prev = "";
    const poll = async () => {
      try {
        const r = await api.get("/jobs/status");
        setJobs(r.data);
        const sig = `${r.data.sync?.state}:${r.data.sync?.imported}:${r.data.analyze?.state}:${r.data.analyze?.done}:${r.data.mail_total}:${r.data.mail_unread}`;
        if (sig !== prev) { prev = sig; load(); loadAccounts(); }
        // Analisi automatica in background: se la casella è caricata (sync non in corso) e restano
        // email da analizzare, avvia l'analisi finché non ha finito tutto (senza intervento manuale).
        if (r.data.sync?.state !== "running" && r.data.analyze?.state !== "running" && (r.data.pending || 0) > 0 && !autoAnalyzeRef.current) {
          autoAnalyzeRef.current = true;
          try { await api.post("/communications/analyze-all"); } catch (e) { console.debug("auto-analyze failed", e); }
          setTimeout(() => { autoAnalyzeRef.current = false; }, 6000);
        }
      } catch (e) {
        console.debug("jobs status poll failed", e);
      }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [load, loadAccounts]);

  // Aggiornamento quasi in tempo reale: il polling leggero (/imap/check-new-light) gira a livello App
  // (vedi App.js) così le nuove email arrivano anche fuori dalla Posta e mostrano un toast globale.
  // Qui la lista si ricarica quando il conteggio cambia (poll /jobs/status ogni 4s) o su evento globale.
  useEffect(() => {
    const onNew = () => { load(); loadAccounts(); };
    window.addEventListener("mailflow:new-mail", onNew);
    return () => window.removeEventListener("mailflow:new-mail", onNew);
  }, [load, loadAccounts]);


  const checkNew = async () => {
    setCheckingNew(true);
    try {
      const r = await api.post("/imap/check-new");
      if (r.data.new > 0) toast.success(`${r.data.new} nuova/e email scaricata/e — analisi AI in corso`);
      else if (r.data.errors?.length) toast.warning(`Nessuna nuova email. Una casella non ha risposto al momento: ${r.data.errors[0]}. Riprova tra poco.`);
      else toast.info("Nessuna nuova email");
      load(); loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel controllo delle email");
    } finally { setCheckingNew(false); }
  };

  // Import automatico all'apertura della Posta: quando l'utente rientra, la prima cosa è scaricare
  // le eventuali nuove email (in background, senza notifiche invasive). Una sola volta per sessione.
  useEffect(() => {
    if (autoCheckRef.current) return;
    autoCheckRef.current = true;
    (async () => {
      try {
        const r = await api.post("/imap/check-new");
        if (r.data?.new > 0) { toast.success(`${r.data.new} nuova/e email scaricata/e`); load(); loadAccounts(); }
      } catch (e) { console.debug("auto check-new failed", e); }
    })();
  }, [load, loadAccounts]);

  const totalUnread = accounts.reduce((s, a) => s + (a.counts?.inbox_unread || 0), 0);
  const pick = (acc, folder) => { setComposeOpen(false); setSelected(null); setActiveAccount(acc); setActiveFolder(folder); setFoldersOpen(false); setQ(""); qRef.current = ""; setShowSuggest(false); setSuggestions([]); };
  const FolderBtn = ({ accId, f, count, unread }) => {
    const Icon = f.icon;
    const active = activeAccount === accId && activeFolder === f.key;
    return (
      <button data-testid={`folder-${accId}-${f.key}`} onClick={() => pick(accId, f.key)}
        className={`w-full flex items-center justify-between pl-9 pr-3 py-1.5 rounded-md text-sm transition-colors duration-150 ${active ? "bg-[#EBF3FF] text-[#0056B3] font-semibold" : "text-gray-600 hover:bg-gray-200/50"}`}>
        <span className="flex items-center gap-2"><Icon className="h-4 w-4" />{f.label}</span>
        {unread > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[10px] font-semibold">{unread}</span>}
        {!unread && count > 0 && <span className="text-[11px] text-gray-400">{count}</span>}
      </button>
    );
  };

  const mailboxPanel = (
    <>
      <div className="px-4 py-4 border-b border-gray-200">
        <h1 className="font-head text-xl font-extrabold tracking-tight">Comunicazioni</h1>
        <Button data-testid="compose-new-button" onClick={openComposeNew} size="sm" className="mt-3 w-full rounded-md h-9 text-xs bg-[#0A0A0A] hover:bg-[#222]">
          <PenSquare className="h-3.5 w-3.5 mr-1.5" />Componi nuova email
        </Button>
        <Button data-testid="check-new-mail-button" onClick={checkNew} disabled={checkingNew} size="sm" className="mt-2 w-full rounded-md h-8 text-xs bg-[#007BFF] hover:bg-[#0056B3]">
          {checkingNew ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}Controlla nuove email
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto scroll-thin p-2">
        <button data-testid="folder-all-inbox" onClick={() => pick("all", "inbox")}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${activeAccount === "all" ? "bg-[#EBF3FF] text-[#0056B3]" : "text-gray-700 hover:bg-gray-200/50"}`}>
          <span className="flex items-center gap-2"><Mails className="h-4 w-4" />Tutte le caselle</span>
          {totalUnread > 0 && <span className="bg-[#007BFF] text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">{totalUnread}</span>}
        </button>
        {accounts.length === 0 && (
          <div className="mt-3">
            <div className="flex items-center gap-2 px-3 py-1.5">
              <div className="h-6 w-6 rounded-md bg-[#0A0A0A] text-white flex items-center justify-center shrink-0"><FileEdit className="h-3.5 w-3.5" /></div>
              <div className="text-xs font-semibold text-gray-800 truncate">Locale (senza account)</div>
            </div>
            <div className="space-y-0.5 mt-0.5">
              <FolderBtn accId="all" f={FOLDERS[2]} count={undefined} unread={0} />
              <FolderBtn accId="all" f={FOLDERS[3]} count={undefined} unread={0} />
              <FolderBtn accId="all" f={FOLDERS[1]} count={undefined} unread={0} />
            </div>
            <button
              data-testid="connect-mailbox-cta"
              onClick={() => navigate("/connetti")}
              className="mt-4 w-full text-left rounded-lg border border-[#007BFF]/40 bg-[#EBF3FF] p-3 hover:bg-[#DBEAFE] transition-colors duration-150 group">
              <div className="flex items-start gap-2.5">
                <div className="h-8 w-8 rounded-md bg-[#007BFF] text-white flex items-center justify-center shrink-0">
                  <Plug className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#0056B3]">Collega una casella email</div>
                  <div className="text-[11px] text-gray-600 mt-0.5 leading-snug">
                    Non hai ancora nessuna casella. Collegane una in "Connetti Account" per ricevere ed elaborare le email con l'AI.
                  </div>
                  <div className="text-[11px] font-semibold text-[#007BFF] mt-1.5 inline-flex items-center gap-1">
                    Connetti Account <span className="transition-transform duration-150 group-hover:translate-x-0.5">→</span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        )}
        {accounts.map((acc) => (
          <div key={acc.id} className="mt-3">
            <div className="flex items-center gap-2 px-3 py-1.5">
              <div className="h-6 w-6 rounded-md bg-[#007BFF] text-white flex items-center justify-center text-[11px] font-bold shrink-0">
                {(acc.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="text-xs font-semibold text-gray-800 truncate" title={acc.email}>{acc.email}</div>
            </div>
            <div className="space-y-0.5 mt-0.5">
              <FolderBtn accId={acc.id} f={FOLDERS[0]} count={acc.counts?.inbox} unread={acc.counts?.inbox_unread} />
              <FolderBtn accId={acc.id} f={FOLDERS[1]} count={acc.counts?.sent} unread={0} />
              <FolderBtn accId={acc.id} f={FOLDERS[2]} count={acc.counts?.drafts} unread={0} />
              <FolderBtn accId={acc.id} f={FOLDERS[3]} count={acc.counts?.scheduled} unread={0} />
              <FolderBtn accId={acc.id} f={FOLDERS[4]} count={acc.counts?.trash} unread={0} />
            </div>
          </div>
        ))}
      </div>
    </>
  );

  useEffect(() => {
    api.get("/trial/status")
      .then((r) => setTrialLimited(!!(r.data && r.data.show_timer)))
      .catch(() => setTrialLimited(false));
  }, []);

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] md:h-[100dvh] w-full bg-[#F8F9FA] overflow-hidden">
      {/* ACCOUNT + FOLDER SIDEBAR (desktop) */}
      <div className="w-[240px] min-w-[240px] hidden md:flex flex-col border-r border-gray-200 bg-gray-50/80 h-full">
        {mailboxPanel}
      </div>

      {/* MESSAGE LIST */}
      <div className={`w-full md:w-[340px] md:min-w-[300px] border-r border-gray-200 bg-white h-full flex-col ${selected ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 pt-4 pb-3 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sheet open={foldersOpen} onOpenChange={setFoldersOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-folders-button" className="md:hidden inline-flex items-center gap-1.5 text-xs rounded-md px-2.5 py-1.5 bg-[#007BFF] text-white font-medium shadow-sm active:bg-[#0069D9]">
                    <Mails className="h-4 w-4" />Caselle
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-[280px] bg-gray-50 flex flex-col">
                  {mailboxPanel}
                </SheetContent>
              </Sheet>
              <div className="text-sm font-semibold text-gray-800 capitalize truncate">{FOLDERS.find((f) => f.key === activeFolder)?.label}</div>
            </div>
            <button
              type="button"
              data-testid="ai-search-toggle"
              onClick={() => { setAiSearch((v) => !v); if (qRef.current) { loadedRef.current = PAGE; } }}
              title="Ricerca AI: trova per significato oltre alle parole esatte, ordinando per pertinenza"
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors duration-150 shrink-0 ${aiSearch ? "bg-gradient-to-r from-[#007BFF] to-[#6D4AFF] text-white border-transparent shadow-sm" : "bg-transparent text-gray-500 border-gray-200 hover:border-[#007BFF] hover:text-[#007BFF]"}`}>
              <Sparkles className="h-3.5 w-3.5" />
              Ricerca AI
            </button>
          </div>
          <form onSubmit={runSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
            <Input data-testid="search-input" value={q}
              onChange={(e) => { setQ(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              placeholder={aiSearch ? "Ricerca AI: descrivi il concetto…" : "Cerca in mail e allegati…"} className="pl-9 rounded-md h-9" autoComplete="off" />
            {showSuggest && suggestions.length > 0 && (
              <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-30 overflow-hidden py-1">
                {suggestions.map((s, i) => (
                  <button key={`${s}-${i}`} type="button" data-testid={`search-suggestion-${i}`}
                    onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#EBF3FF] flex items-center gap-2 transition-colors duration-100">
                    <Search className="h-3.5 w-3.5 text-gray-400 shrink-0" /><span className="truncate">{s}</span>
                  </button>
                ))}
              </div>
            )}
          </form>
          {aiSearch && (
            <div className="flex items-center justify-end mt-1.5">
              <span data-testid="ai-search-hint" className="text-[10px] text-gray-400 flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" />ricerca per significato · ordina per pertinenza</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 mt-3 flex-nowrap">
            {isInbox && (
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="category-filter-select" className="h-8 text-xs flex-1 min-w-0 rounded-md">
                  <Filter className="h-3.5 w-3.5 mr-1 text-gray-400 shrink-0" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["tutti", "da_analizzare", ...filterCatKeys].map((f) => (
                    <SelectItem key={f} value={f} className="text-xs">{f === "tutti" ? "Tutte le categorie" : catLabel(f)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {isInbox && (
              <Select value={readFilter} onValueChange={setReadFilter}>
                <SelectTrigger data-testid="read-filter-select" className="h-8 text-xs w-[92px] shrink-0 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">Tutte</SelectItem>
                  <SelectItem value="unread" className="text-xs">Non lette</SelectItem>
                  <SelectItem value="read" className="text-xs">Lette</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!isInbox && <div className="flex-1" />}
            <button data-testid="select-mode-toggle" title="Seleziona" onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border transition-colors duration-150 ${selectMode ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-transparent text-gray-500 border-gray-200 hover:border-gray-400"}`}>
              <CheckSquare className="h-3.5 w-3.5" />
            </button>
          </div>
          {isInbox && (
            <div className="flex items-center gap-2 mt-2.5 flex-nowrap overflow-x-auto scroll-thin pb-0.5 -mx-1 px-1" data-testid="priority-filter-pills">
              {[{ key: "all", label: "Tutte", active: "bg-[#007BFF] text-white", dot: "" },
                { key: "alta", label: "Alta", active: "bg-red-500 text-white", dot: "bg-red-500" },
                { key: "media", label: "Media", active: "bg-amber-500 text-white", dot: "bg-amber-500" },
                { key: "bassa", label: "Bassa", active: "bg-slate-400 text-white", dot: "bg-slate-400" },
              ].map((p) => {
                const on = priorityFilter === p.key;
                return (
                  <button
                    key={p.key}
                    data-testid={`priority-pill-${p.key}`}
                    onClick={() => setPriorityFilter(p.key)}
                    className={`shrink-0 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors duration-150 ${on ? p.active + " shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    {p.dot && <span className={`h-2 w-2 rounded-full ${on ? "bg-white" : p.dot}`} />}
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {(jobs.sync?.state === "running" || jobs.analyze?.state === "running") && (
          <div data-testid="jobs-progress" className="px-4 py-3 border-b border-gray-200 bg-[#007BFF]/5 space-y-2.5">
            {jobs.sync?.state === "running" && <ProgressRow label="Importazione email…" mailbox={jobs.sync.account_label} seqIndex={jobs.sync.account_index} seqTotal={jobs.sync.account_total} done={jobs.sync.imported || 0} total={jobs.sync.total || 0} />}
            {jobs.analyze?.state === "running" && <ProgressRow label="Analisi AI…" mailbox={jobs.analyze.account_label} done={jobs.analyze.done || 0} total={jobs.analyze.total || 0} />}
          </div>
        )}
        {selectMode && (
          <div data-testid="bulk-action-bar" className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-[#EBF3FF]">
            <button data-testid="bulk-select-all" onClick={toggleSelectAll} className="text-xs font-medium text-[#0056B3] flex items-center gap-1.5">
              <span className={`h-4 w-4 rounded border flex items-center justify-center ${allChecked ? "bg-[#007BFF] border-[#007BFF]" : "border-gray-400 bg-white"}`}>
                {allChecked && <CheckSquare className="h-3 w-3 text-white" />}
              </span>
              {checked.length ? `${checked.length} selezionate` : "Seleziona tutte"}
            </button>
            <div className="flex-1" />
            <Button data-testid="bulk-reply-all-button" onClick={bulkReplyAll} disabled={!checked.length || bulkBusy} size="sm" variant="outline" className="rounded-md h-7 text-[11px]">
              <ReplyAll className="h-3.5 w-3.5 mr-1" />Rispondi a tutti
            </Button>
            <Button data-testid="bulk-read-button" onClick={() => bulkMarkRead(true)} disabled={!checked.length || bulkBusy} size="sm" variant="outline" className="rounded-md h-7 text-[11px]">
              <MailCheck className="h-3.5 w-3.5 mr-1" />Letta
            </Button>
            <Button data-testid="bulk-unread-button" onClick={() => bulkMarkRead(false)} disabled={!checked.length || bulkBusy} size="sm" variant="outline" className="rounded-md h-7 text-[11px]">
              <Mail className="h-3.5 w-3.5 mr-1" />Non letta
            </Button>
            <Button data-testid="bulk-delete-button" onClick={bulkDelete} disabled={!checked.length || bulkBusy} size="sm" className="rounded-md h-7 text-[11px] bg-red-600 hover:bg-red-700">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          </div>
        )}
        {trialLimited && accounts.length > 0 && (
          <div
            data-testid="trial-import-limit-notice"
            className="flex items-start gap-2.5 px-4 py-2.5 border-b border-amber-200 bg-amber-50 text-amber-900"
          >
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <p className="text-[12px] leading-snug">
              Stai vedendo solo le <span className="font-semibold">ultime 50 email per cartella</span>.{" "}
              <button
                data-testid="trial-import-limit-cta"
                onClick={() => navigate("/profilo")}
                className="font-semibold underline underline-offset-2 hover:text-amber-700"
              >
                Completa lo Studio
              </button>{" "}
              per importarle tutte.
            </p>
          </div>
        )}
        <EmailList
          items={filteredItems} total={total} selectedId={selected?.id} isInbox={isInbox} selectMode={selectMode}
          checked={checked} leavingIds={leavingIds} activeFolder={activeFolder} threadCounts={threadCounts}
          hasMore={hasMore} loadingMore={loadingMore} listScrollRef={listScrollRef} onListScroll={onListScroll}
          catStyle={catStyle} catLabel={catLabel} onSelect={select} onToggleChecked={toggleChecked}
          highlight={highlightTerm}
          onOpenThread={openThread} onReply={onReply} onReplyAll={onReplyAll} onForward={onForward}
          onDelete={deleteItem} onToggleReadItem={toggleReadItem}
        />
      </div>

      {/* DETAIL */}
      <EmailDetail
        selected={selected} isInbox={isInbox} isTrash={activeFolder === "trash"} busy={busy} detailLoading={detailLoading} showHtml={showHtml}
        htmlHeight={htmlHeight} replyAll={replyAll} draft={draft} rewritingReply={rewritingReply} appliedDraftTone={appliedDraftTone}
        setSelected={setSelected} setShowHtml={setShowHtml} setHtmlHeight={setHtmlHeight} setReplyAll={setReplyAll} setDraft={setDraft}
        onToggleRead={toggleRead} onChangePriority={changePriority} onDeleteMail={deleteMail} onRestoreMail={restoreMail}
        onRewriteDraft={rewriteDraftTone} onApprove={approve} onSaveDraft={saveDraft} onCreateTicket={createTicket} onOpenPratica={openPratica}
        onReply={onReply} onReplyAll={onReplyAll} onForward={onForward}
        catStyle={catStyle} catLabel={catLabel} highlight={highlightTerm}
        signatureHtml={accounts.find((x) => x.id === selected?.account_id)?.signature_html || ""}
      />

      <ReplyDialog
        compose={compose} setCompose={setCompose} onClose={() => setCompose(null)} onSend={sendCompose}
        sending={sending} rewritingReply={rewritingReply} appliedReplyTone={appliedReplyTone} onRewrite={rewriteReplyTone}
      />

      <ComposeDialog open={composeOpen} onClose={() => setComposeOpen(false)} onSent={composeSent} initial={composeInit} />

      <ThreadDialog
        open={threadOpen} onOpenChange={setThreadOpen} title={threadTitle} loading={threadLoading}
        msgs={threadMsgs} catStyle={catStyle} catLabel={catLabel}
        onOpenMessage={(m) => { setThreadOpen(false); select(m); }}
      />
    </div>
  );
}
